import clsx from 'clsx';
import { FiltersModal } from 'components/FiltersModal/FiltersModal';
import { useFiltersModalContext } from 'components/FiltersModalProvider/FiltersModalProvider';
import { useFiltersContext } from 'components/FiltersProvider/FiltersProvider';
import { useMedia } from 'hooks/useMedia';
import { useTheme } from 'hooks/useTheme';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sizes } from 'styles/sizes';
import { noScroll } from 'theme/base.css';
import { BaseCruiseSearchWidget } from './BaseCruiseSearchWidget/BaseCruiseSearchWidget';
import styles from './CruiseSearchWidget.css';
import { HomeCruiseSearchWidget } from './HomeCruiseSearchWidget/HomeCruiseSearchWidget';
import { DEFAULT_FILTER_KEY_TO_CUSTOM_FILTER_CONFIG } from './constants';
import { CruiseSearchWidgetProps, CustomFilterConfig, FilterDropdownConfig, Variant } from './types';
import { FiltersFlagged, getEnabledFilters } from './utils/enabledFilters';

const cruiseSearchWidgetVariants: { [key in Variant]: React.ComponentType<CruiseSearchWidgetProps> } = {
	[Variant.Base]: BaseCruiseSearchWidget,
	[Variant.Home]: HomeCruiseSearchWidget,
};

const variantToClass: { [key in Variant]: string } = {
	[Variant.Base]: styles.BASE_VARIANT,
	[Variant.Home]: styles.HOME_VARIANT,
};

export function CruiseSearchWidget({ variant = Variant.Base, customElements, enableNumberOfGuests, ...props }: Readonly<CruiseSearchWidgetProps>) {
	const theme = useTheme();

	const { isSticky, navigationFilters, enableUniqueFilters, scrollingContainer = 'html' } = props;

	const {
		setActiveFilter,
		resetSelectionToApplied,
		setEnabledFilters,
		setEnableUniqueFilters,
		activeFilter,
		setFilterKeyToCustomFilterConfig,
		featureFlags,
	} = useFiltersContext();
	const { showFiltersModal, setShowFiltersModal, resetNumberOfGuestsSelectionToApplied } = useFiltersModalContext();
	const isDesktop = useMedia(`(min-width: ${sizes.desktop}px)`);
	const rootRef = useRef<HTMLDivElement>(null);
	const [isPinned, setIsPinned] = useState(false);

	useEffect(() => {
		const flagControlledFilters: FiltersFlagged = {
			voyageType: featureFlags.enableRiverCruises,
			numberOfGuests: enableNumberOfGuests ?? false,
		};

		const enabledFilters = getEnabledFilters(navigationFilters, variant, customElements ?? [], flagControlledFilters);

		const customFilterConfig = customElements?.reduce((acum: Record<string, CustomFilterConfig>, item) => {
			if (item.customFilterConfig) {
				acum[item.key] = item.customFilterConfig;
			}
			return acum;
		}, {});

		setEnabledFilters(enabledFilters as FilterDropdownConfig[]);
		setFilterKeyToCustomFilterConfig({
			...DEFAULT_FILTER_KEY_TO_CUSTOM_FILTER_CONFIG,
			...customFilterConfig,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleScroll = useCallback(() => {
		const top = rootRef?.current?.getBoundingClientRect().top;
		if (top !== undefined) {
			setIsPinned(Number(top.toFixed()) <= 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rootRef?.current?.getBoundingClientRect().top]);

	const handleOverlayClick = useCallback(
		(e?: React.MouseEvent<HTMLDivElement>) => {
			e?.stopPropagation();
			resetSelectionToApplied();
			resetNumberOfGuestsSelectionToApplied();
			setShowFiltersModal(false);
			setActiveFilter(null);
		},
		[setShowFiltersModal, setActiveFilter, resetSelectionToApplied, resetNumberOfGuestsSelectionToApplied],
	);

	useEffect(() => {
		if (isSticky) {
			window.addEventListener('scroll', handleScroll);
		}
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll, isSticky]);

	useEffect(() => {
		if (showFiltersModal) {
			document.querySelector(scrollingContainer)?.classList.add(noScroll);
		} else {
			document.querySelector(scrollingContainer)?.classList.remove(noScroll);
		}
	}, [showFiltersModal, scrollingContainer]);

	useEffect(() => {
		setEnableUniqueFilters(enableUniqueFilters ?? false);
	}, [enableUniqueFilters, setEnableUniqueFilters]);

	const CruiseSearchWidgetVariantComponent = useMemo(() => {
		return cruiseSearchWidgetVariants[variant];
	}, [variant]);

	return (
		<div
			ref={rootRef}
			className={clsx([
				theme,
				styles.root,
				variantToClass[variant],
				{
					[styles.isSticky]: isSticky,
					[styles.isModalOpened]: showFiltersModal,
					[styles.resetIndex]: !showFiltersModal && !isPinned,
				},
			])}
		>
			<CruiseSearchWidgetVariantComponent variant={variant} isPinned={isPinned} {...props} />
			{showFiltersModal && !activeFilter?.skipModal && (
				<>
					<FiltersModal variant={variant} isPinned={isPinned} />
					{isDesktop && <div className={styles.modalOverlay} onClick={(e) => handleOverlayClick(e)} onKeyDown={() => handleOverlayClick()}></div>}
				</>
			)}
		</div>
	);
}
